import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit, Power, PowerOff } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { SSOProvider } from '../../types';
import { getAllSSOProviders, deleteSSOProvider, updateSSOProvider } from '../../utils/auth/authService';

const SSOProvidersPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [providers, setProviders] = useState<SSOProvider[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const data = await getAllSSOProviders();
      setProviders(data);
    } catch (err) {
      console.error('Failed to load SSO providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (provider: SSOProvider) => {
    try {
      await updateSSOProvider(provider.provider_id, { enabled: !provider.enabled });
      await loadProviders();
    } catch (err) {
      alert('Failed to update provider');
    }
  };

  const handleDelete = async (providerId: string) => {
    if (!window.confirm('Are you sure you want to delete this SSO provider?')) return;
    try {
      await deleteSSOProvider(providerId);
      await loadProviders();
    } catch (err) {
      alert('Failed to delete provider');
    }
  };

  if (loading) {
    return <div style={{ color: theme.text }}>Loading...</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 24, margin: 0 }}>SSO Providers</h1>
        <button
          onClick={() => navigate('/auth/sso-providers/add')}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 500,
          }}
        >
          <Plus size={18} />
          Add Provider
        </button>
      </div>

      {providers.length === 0 ? (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: 48,
          textAlign: 'center',
        }}>
          <p style={{ color: theme.textSecondary, fontSize: 16, margin: 0 }}>
            No SSO providers configured
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {providers.map((provider) => (
            <div
              key={provider.provider_id}
              onClick={() => navigate(`/auth/sso-providers/${provider.provider_id}`)}
              style={{
                background: theme.cardBackground,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: 20,
                cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.accent}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = theme.border}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <h3 style={{ color: theme.text, fontSize: 18, margin: 0 }}>
                      {provider.name}
                    </h3>
                    <span style={{
                      padding: '4px 8px',
                      background: provider.provider_type === 'oidc' ? '#e0f2fe' : '#f0e6ff',
                      color: provider.provider_type === 'oidc' ? '#0369a1' : '#7c3aed',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>
                      {provider.provider_type}
                    </span>
                    {provider.enabled ? (
                      <span style={{
                        padding: '4px 8px',
                        background: '#d1fae5',
                        color: '#065f46',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        Enabled
                      </span>
                    ) : (
                      <span style={{
                        padding: '4px 8px',
                        background: '#fee2e2',
                        color: '#991b1b',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        Disabled
                      </span>
                    )}
                  </div>
                  
                  <div style={{ color: theme.textSecondary, fontSize: 14, marginBottom: 8 }}>
                    {provider.provider_type === 'oidc' && provider.oidc_config && (
                      <div>
                        <strong>Discovery URL:</strong> {provider.oidc_config.discovery_url}
                      </div>
                    )}
                    {provider.provider_type === 'saml' && provider.saml_config && (
                      <div>
                        <strong>Entity ID:</strong> {provider.saml_config.entity_id}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ color: theme.textSecondary, fontSize: 12 }}>
                    Auto-create users: {provider.auto_create_users ? 'Yes' : 'No'}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleEnabled(provider);
                    }}
                    style={{
                      padding: 8,
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      color: theme.text,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title={provider.enabled ? 'Disable' : 'Enable'}
                  >
                    {provider.enabled ? <PowerOff size={16} /> : <Power size={16} />}
                  </button>
                  <button
                    onClick={() => navigate(`/auth/sso-providers/${provider.provider_id}`)}
                    style={{
                      padding: 8,
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      color: theme.text,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="View/Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(provider.provider_id);
                    }}
                    style={{
                      padding: 8,
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      color: '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SSOProvidersPage;
